import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function EmployerForm() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    role_title: "",
    seniority: "mid",
    location: "germany",
    headcount: 1,
    tech_stack: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/employer", {
        ...form,
        headcount: Number(form.headcount) || 1,
      });
      setDone(true);
      toast.success("Request received — a senior recruiter will reach out within 24h.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="employer-form-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-emerald-900"
      >
        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        <div className="font-display font-bold text-xl mt-3">
          Thanks, {form.contact_name.split(" ")[0] || "there"}.
        </div>
        <p className="mt-2 text-emerald-800/80">
          We've received your hiring request for <b>{form.role_title}</b>. A
          senior recruiter will reach out to {form.email} within 24 hours with a
          shortlist preview.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="employer-form"
      onSubmit={submit}
      className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company">
          <Input
            data-testid="emp-company"
            required
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
          />
        </Field>
        <Field label="Contact name">
          <Input
            data-testid="emp-contact"
            required
            value={form.contact_name}
            onChange={(e) => update("contact_name", e.target.value)}
          />
        </Field>
        <Field label="Work email">
          <Input
            data-testid="emp-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>
        <Field label="Phone (optional)">
          <Input
            data-testid="emp-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </Field>
        <Field label="Role title">
          <Input
            data-testid="emp-role"
            required
            placeholder="e.g. Senior SAP S/4HANA Consultant"
            value={form.role_title}
            onChange={(e) => update("role_title", e.target.value)}
          />
        </Field>
        <Field label="Seniority">
          <Select
            value={form.seniority}
            onValueChange={(v) => update("seniority", v)}
          >
            <SelectTrigger data-testid="emp-seniority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="junior">Junior</SelectItem>
              <SelectItem value="mid">Mid</SelectItem>
              <SelectItem value="senior">Senior</SelectItem>
              <SelectItem value="lead">Lead / Principal</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Country">
          <Select
            value={form.location}
            onValueChange={(v) => update("location", v)}
          >
            <SelectTrigger data-testid="emp-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="germany">Germany</SelectItem>
              <SelectItem value="netherlands">Netherlands</SelectItem>
              <SelectItem value="romania">Romania</SelectItem>
              <SelectItem value="poland">Poland</SelectItem>
              <SelectItem value="bulgaria">Bulgaria</SelectItem>
              <SelectItem value="remote-europe">Remote Europe</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Headcount">
          <Input
            data-testid="emp-headcount"
            type="number"
            min={1}
            value={form.headcount}
            onChange={(e) => update("headcount", e.target.value)}
          />
        </Field>
      </div>
      <Field label="Tech stack / must-have skills">
        <Input
          data-testid="emp-stack"
          placeholder="React, Node.js, AWS…"
          value={form.tech_stack}
          onChange={(e) => update("tech_stack", e.target.value)}
        />
      </Field>
      <Field label="Notes (optional)">
        <Textarea
          data-testid="emp-notes"
          rows={3}
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
        />
      </Field>
      <Button
        data-testid="emp-submit"
        type="submit"
        disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
      >
        {loading ? "Submitting…" : "Request Shortlist"}
      </Button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
